import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BLANK = { company: "", role: "", start_date: "", end_date: "", location: "", description: "", highlights: [], order: 0 };

export default function AdminTimeline() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/timeline").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setDraft(BLANK); setEditingId(null); setOpen(true); };
  const openEdit = (e) => { setDraft({ ...e, end_date: e.end_date || "", highlights: e.highlights || [] }); setEditingId(e.id); setOpen(true); };

  const save = async () => {
    const payload = { ...draft, end_date: draft.end_date || null };
    try {
      if (editingId) await api.put(`/timeline/${editingId}`, payload);
      else await api.post("/timeline", payload);
      setOpen(false);
      toast.success("Saved.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await api.delete(`/timeline/${id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <header className="mb-8 flex items-baseline justify-between">
        <div>
          <p className="label-xs">Career Timeline</p>
          <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Experience</h1>
        </div>
        <Button onClick={openNew} data-testid="timeline-new"><Plus className="h-4 w-4" /> Add event</Button>
      </header>

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left label-xs">
              <th className="p-3">Role</th>
              <th className="p-3">Company</th>
              <th className="p-3">Period</th>
              <th className="p-3 w-20">Order</th>
              <th className="p-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-t border-border">
                <td className="p-3 font-medium cursor-pointer" onClick={() => openEdit(e)}>{e.role}</td>
                <td className="p-3 text-muted-foreground">{e.company}</td>
                <td className="p-3 font-mono text-xs text-muted-foreground">{e.start_date} — {e.end_date || "Present"}</td>
                <td className="p-3 font-mono text-xs">{e.order}</td>
                <td className="p-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(e)}>Edit</Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(e.id)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <FF label="Company" v={draft.company} on={(v) => setDraft({ ...draft, company: v })} />
              <FF label="Role" v={draft.role} on={(v) => setDraft({ ...draft, role: v })} />
              <FF label="Start (YYYY-MM)" v={draft.start_date} on={(v) => setDraft({ ...draft, start_date: v })} />
              <FF label="End (YYYY-MM, blank = present)" v={draft.end_date} on={(v) => setDraft({ ...draft, end_date: v })} />
              <FF label="Location" v={draft.location} on={(v) => setDraft({ ...draft, location: v })} />
              <FF label="Order" type="number" v={draft.order} on={(v) => setDraft({ ...draft, order: Number(v) })} />
            </div>
            <div>
              <Label className="label-xs">Description</Label>
              <Textarea rows={3} className="mt-2" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </div>
            <div>
              <Label className="label-xs">Highlights (one per line)</Label>
              <Textarea rows={5} className="mt-2 font-mono text-sm" value={(draft.highlights || []).join("\n")} onChange={(e) => setDraft({ ...draft, highlights: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save}><Save className="h-4 w-4" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FF({ label, v, on, type = "text" }) {
  return (
    <div>
      <Label className="label-xs">{label}</Label>
      <Input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-2" />
    </div>
  );
}
